import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertCircle, Sparkles, ShieldCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ProductFormData } from "@/types/product";
import { DEFAULT_PRODUCT_FORM, defaultPricingFromMrp } from "@/types/product";
import { useStorefrontCategories } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";
import MultiImageUpload from "@/components/shared/MultiImageUpload";
import { UploadFolder } from "@/services/filesService";
import { cn } from "@/lib/utils";

export type ProductPricePanel = "store" | "admin";

function buildProductSchema(options?: {
  enforceStoreBv?: boolean;
  isEdit?: boolean;
  availableBv?: number;
  oldTotalBv?: number;
}) {
  const schema = z.object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    description: z.string().optional(),
    categoryId: z.string().min(1, "Category is required"),
    mrp: z.coerce.number().min(0),
    dp: z.coerce.number().min(0).optional().default(0),
    gstPercent: z.coerce.number().min(0).max(100),
    wholesalePrice: z.coerce.number().min(0).optional().default(0),
    retailPrice: z.coerce.number().min(0).optional().default(0),
    customerPrice: z.coerce.number().min(0),
    deliveryCharge: z.coerce.number().min(0),
    distributorCommissionPercent: z.coerce.number().min(0).max(100).optional().default(0),
    wholesaleCommissionPercent: z.coerce.number().min(0).max(100).optional().default(0),
    retailCommissionPercent: z.coerce.number().min(0).max(100).optional().default(0),
    businessVolume: z.coerce.number().min(options?.enforceStoreBv ? 0.01 : 0, {
      message: options?.enforceStoreBv ? "Business volume must be greater than zero" : undefined,
    }),
    sellingPrice: z.coerce.number().min(0),
    stockQuantity: z.coerce.number().int().min(0, "Stock quantity cannot be negative"),
    unit: z.string().min(1),
    sku: z.string().optional(),
    isVeg: z.boolean(),
    tags: z.string().optional(),
    imageUrl: z.string().optional(),
    imageUrls: z.array(z.string()).min(1, "At least one product image is required"),
  });

  if (!options?.enforceStoreBv) return schema;

  return schema.superRefine((data, ctx) => {
    const available = options.availableBv ?? 0;
    const newTotal = Number((data.businessVolume * data.stockQuantity).toFixed(2));

    if (!options.isEdit) {
      if (newTotal > available) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["businessVolume"],
          message: `Total required BV (${newTotal}) exceeds available store BV (${available}). Required = ${data.businessVolume} BV × ${data.stockQuantity} qty.`,
        });
      }
    } else {
      const oldTotal = options.oldTotalBv ?? 0;
      const diff = Number((newTotal - oldTotal).toFixed(2));
      if (diff > 0 && diff > available) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["businessVolume"],
          message: `Additional required BV (${diff}) exceeds available store BV (${available}).`,
        });
      }
    }
  });
}

interface ProductFormProps {
  defaultValues?: ProductFormData;
  onSubmit: (data: ProductFormData) => void;
  onCancel: () => void;
  submitLabel?: string;
  isEdit?: boolean;
  availableBv?: number;
  currentProductBv?: number;
  currentProductStock?: number;
  enforceStoreBv?: boolean;
  pricePanel?: ProductPricePanel;
  isAdminProduct?: boolean;
}

const ProductForm = ({
  defaultValues = DEFAULT_PRODUCT_FORM,
  onSubmit,
  onCancel,
  submitLabel = "Save Product",
  isEdit = false,
  availableBv,
  currentProductBv = 0,
  currentProductStock = 0,
  enforceStoreBv = false,
  pricePanel = "store",
  isAdminProduct = false,
}: ProductFormProps) => {
  const isAdminPricing = pricePanel === "admin";
  const oldBv = currentProductBv ?? 0;
  const oldStock = currentProductStock ?? 0;
  const oldTotalBv = Number((oldBv * oldStock).toFixed(2));

  const shouldEnforceBv = enforceStoreBv && !isAdminProduct;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(buildProductSchema({ enforceStoreBv: shouldEnforceBv, isEdit, availableBv, oldTotalBv })),
    defaultValues,
  });

  const isVeg = watch("isVeg");
  const imageUrls = watch("imageUrls") ?? [];
  const mrp = watch("mrp");
  const customerPrice = watch("customerPrice");
  const watchedBv = Number(watch("businessVolume")) || 0;
  const watchedStock = Number(watch("stockQuantity")) || 0;
  const totalRequiredBv = Number((watchedBv * watchedStock).toFixed(2));
  const bvDiff = Number((totalRequiredBv - oldTotalBv).toFixed(2));
  const isInsufficientBv = enforceStoreBv
    ? isEdit
      ? bvDiff > 0 && bvDiff > (availableBv ?? 0)
      : totalRequiredBv > (availableBv ?? 0)
    : false;
  const { data: categories = [], isLoading: isLoadingCategories } = useStorefrontCategories();

  const autoFillPricing = () => {
    const derived = defaultPricingFromMrp(Number(mrp) || 0);
    if (isAdminPricing) {
      setValue("dp", derived.dp);
      setValue("wholesalePrice", derived.wholesalePrice);
      setValue("retailPrice", derived.retailPrice);
    }
    setValue("customerPrice", derived.customerPrice);
    setValue("sellingPrice", derived.sellingPrice);
    setValue("deliveryCharge", derived.deliveryCharge);
    setValue("businessVolume", derived.businessVolume);
    if (!watch("gstPercent")) setValue("gstPercent", 18);
  };

  const handleFormSubmit = (data: ProductFormData) => {
    const price = data.customerPrice || data.sellingPrice || data.mrp;
    onSubmit({
      ...data,
      dp: isAdminPricing ? data.dp : data.dp || 0,
      wholesalePrice: isAdminPricing ? data.wholesalePrice : data.wholesalePrice || 0,
      retailPrice: isAdminPricing ? data.retailPrice : data.retailPrice || 0,
      customerPrice: price,
      sellingPrice: price,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {isAdminProduct && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-bold text-foreground">Admin-Created Product (Wholesale Channel)</p>
            <p className="text-xs text-muted-foreground">
              This product was purchased through the wholesale supply chain. Product specifications, pricing, BV, and stock quantity are fully locked. Stock increases automatically only when you purchase more from Wholesaler via Store Supply.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Product Name * {isAdminProduct && <Lock className="inline h-3 w-3 text-muted-foreground ml-1" />}</Label>
            <Input id="name" {...register("name")} placeholder="Fresh Tomatoes" disabled={isAdminProduct} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description {isAdminProduct && <Lock className="inline h-3 w-3 text-muted-foreground ml-1" />}</Label>
            <Textarea id="description" {...register("description")} placeholder="Product description..." rows={3} disabled={isAdminProduct} />
          </div>

          <div className="space-y-2">
            <Label>Category * {isAdminProduct && <Lock className="inline h-3 w-3 text-muted-foreground ml-1" />}</Label>
            {isLoadingCategories ? (
              <Skeleton className="h-10 w-full rounded-md" />
            ) : (
              <Select value={watch("categoryId")} onValueChange={(v) => setValue("categoryId", v, { shouldValidate: true })} disabled={isAdminProduct}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base flex items-center gap-1.5">
                  Product Price {isAdminProduct && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                </CardTitle>
                {!isAdminProduct && (
                  <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto" onClick={autoFillPricing}>
                    Auto-fill from MRP
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">MRP – Maximum Retail Price (₹)</Label>
                <Input type="number" {...register("mrp")} disabled={isAdminProduct} />
              </div>

              {isAdminPricing && (
                <div className="space-y-1">
                  <Label className="text-xs">DP – Distributor Price (₹)</Label>
                  <Input type="number" {...register("dp")} disabled={isAdminProduct} />
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-xs">GST – Goods and Services Tax (%)</Label>
                <Input type="number" {...register("gstPercent")} disabled={isAdminProduct} />
              </div>

              {isAdminPricing && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs">WP – Wholesale Price (₹)</Label>
                    <Input type="number" {...register("wholesalePrice")} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">RP – Retail Price (₹)</Label>
                    <Input type="number" {...register("retailPrice")} />
                  </div>
                </>
              )}

              <div className="space-y-1">
                <Label className="text-xs">CP – Customer Price (₹)</Label>
                <Input
                  type="number"
                  disabled={isAdminProduct}
                  {...register("customerPrice", {
                    onChange: (e) => {
                      const value = Number(e.target.value) || 0;
                      setValue("sellingPrice", value);
                    },
                  })}
                />
                {Number(customerPrice) <= 0 && Number(mrp) > 0 ? (
                  <p className="text-[11px] text-amber-600">Set customer price, or tap Auto-fill from MRP</p>
                ) : null}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">DC – Delivery Charge (₹)</Label>
                <Input type="number" disabled={isAdminProduct} {...register("deliveryCharge")} />
              </div>

              {isAdminPricing && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs">Distributor commission (%)</Label>
                    <Input type="number" min={0} max={100} step="0.01" disabled={isAdminProduct} {...register("distributorCommissionPercent")} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Wholesale commission (%)</Label>
                    <Input type="number" min={0} max={100} step="0.01" disabled={isAdminProduct} {...register("wholesaleCommissionPercent")} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Retail commission (%)</Label>
                    <Input type="number" min={0} max={100} step="0.01" disabled={isAdminProduct} {...register("retailCommissionPercent")} />
                  </div>
                </>
              )}

              <div className="space-y-1">
                <Label className="text-xs">BV – Business Volume (per unit)</Label>
                <Input type="number" step="0.01" min={0} disabled={isAdminProduct} {...register("businessVolume")} />
                {errors.businessVolume ? (
                  <p className="text-xs text-destructive">{errors.businessVolume.message}</p>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="stockQuantity">
                Stock Quantity * {isAdminProduct && <Lock className="inline h-3 w-3 text-muted-foreground ml-1" />}
              </Label>
              <Input
                id="stockQuantity"
                type="number"
                {...register("stockQuantity")}
                disabled={isAdminProduct}
              />
              {isAdminProduct ? (
                <p className="text-[11px] text-muted-foreground">
                  Purchased stock is locked. To add more stock, order from Wholesaler via Store Supply.
                </p>
              ) : errors.stockQuantity ? (
                <p className="text-xs text-destructive">{errors.stockQuantity.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit">Unit * {isAdminProduct && <Lock className="inline h-3 w-3 text-muted-foreground ml-1" />}</Label>
              <Select value={watch("unit")} onValueChange={(v) => setValue("unit", v)} disabled={isAdminProduct}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="piece">Piece</SelectItem>
                  <SelectItem value="kg">Kg</SelectItem>
                  <SelectItem value="g">Gram</SelectItem>
                  <SelectItem value="L">Litre</SelectItem>
                  <SelectItem value="pack">Pack</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {enforceStoreBv && (
            <div
              className={cn(
                "rounded-xl border p-4 transition-colors space-y-3",
                isInsufficientBv
                  ? "border-destructive/60 bg-destructive/5 text-destructive"
                  : isEdit && bvDiff < 0
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : isEdit && bvDiff > 0
                  ? "border-amber-500/40 bg-amber-500/5"
                  : "border-border/80 bg-muted/30"
              )}
            >
              <div className="flex items-center justify-between border-b border-border/50 pb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" /> Store BV Calculation
                </span>
                <span className="text-xs font-medium text-muted-foreground">
                  Available: <strong className="text-foreground">{availableBv ?? 0} BV</strong>
                </span>
              </div>

              {!isEdit ? (
                // Create Mode
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center text-muted-foreground">
                    <span>Required calculation:</span>
                    <span className="font-mono text-foreground">{watchedBv} BV × {watchedStock} qty</span>
                  </div>
                  <div className="flex justify-between items-center font-semibold pt-1 border-t border-border/40">
                    <span className="text-foreground">Total Required BV:</span>
                    <span className="font-mono text-sm text-foreground">{totalRequiredBv} BV</span>
                  </div>
                  {isInsufficientBv ? (
                    <p className="text-xs font-medium text-destructive pt-1 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      Insufficient store BV. Required: {totalRequiredBv} BV, Available: {availableBv ?? 0} BV.
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground pt-1">
                      Remaining balance after deduction: <strong className="text-foreground">{(Number(availableBv ?? 0) - totalRequiredBv).toFixed(2)} BV</strong>
                    </p>
                  )}
                </div>
              ) : (
                // Edit Mode
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-2 pb-2 border-b border-border/40 text-muted-foreground">
                    <div>
                      <span className="block text-[11px]">Current Total BV:</span>
                      <span className="font-mono font-medium text-foreground">{oldBv} BV × {oldStock} = {oldTotalBv} BV</span>
                    </div>
                    <div>
                      <span className="block text-[11px]">New Total BV:</span>
                      <span className="font-mono font-medium text-foreground">{watchedBv} BV × {watchedStock} = {totalRequiredBv} BV</span>
                    </div>
                  </div>

                  {bvDiff > 0 ? (
                    <div className="space-y-1 pt-1">
                      <div className="flex justify-between items-center font-semibold">
                        <span className="text-amber-700 dark:text-amber-400">Additional BV Required:</span>
                        <span className="font-mono text-sm text-amber-700 dark:text-amber-400">+{bvDiff} BV</span>
                      </div>
                      {isInsufficientBv ? (
                        <p className="text-xs font-medium text-destructive flex items-center gap-1 pt-1">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                          Insufficient store BV. Needs additional +{bvDiff} BV, but only {availableBv ?? 0} BV is available.
                        </p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">
                          Deducts {bvDiff} BV from available balance. Remaining: <strong className="text-foreground">{(Number(availableBv ?? 0) - bvDiff).toFixed(2)} BV</strong>
                        </p>
                      )}
                    </div>
                  ) : bvDiff < 0 ? (
                    <div className="space-y-1 pt-1">
                      <div className="flex justify-between items-center font-semibold text-emerald-700 dark:text-emerald-400">
                        <span>BV to be Returned to Store:</span>
                        <span className="font-mono text-sm">+{Math.abs(bvDiff)} BV</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {Math.abs(bvDiff)} BV will be refunded back to your store available BV balance!
                      </p>
                    </div>
                  ) : (
                    <div className="pt-1 flex items-center justify-between text-muted-foreground">
                      <span>BV Balance Impact:</span>
                      <span className="font-medium text-foreground">No change (0 BV difference)</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="sku">SKU</Label><Input id="sku" {...register("sku")} disabled={isAdminProduct} /></div>
            <div className="space-y-2"><Label htmlFor="tags">Tags</Label><Input id="tags" {...register("tags")} disabled={isAdminProduct} /></div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <Label htmlFor="isVeg">Vegetarian Product</Label>
            <Switch id="isVeg" checked={isVeg} onCheckedChange={(v) => setValue("isVeg", v)} disabled={isAdminProduct} />
          </div>
        </div>

        <div className="space-y-4">
          <MultiImageUpload
            label="Product images *"
            value={imageUrls}
            onChange={(urls) => {
              if (isAdminProduct) return;
              setValue("imageUrls", urls, { shouldValidate: true });
              setValue("imageUrl", urls[0] ?? "", { shouldValidate: true });
            }}
            folder={UploadFolder.Products}
            maxImages={6}
            disabled={isAdminProduct}
          />
          {errors.imageUrls && <p className="text-xs text-destructive">{errors.imageUrls.message}</p>}
          <p className="text-xs text-muted-foreground">
            B.V is distributed: 30% self shopping cashback, 40% reward, 27% all levels (configurable in admin).
          </p>
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3 items-center">
        {isInsufficientBv && (
          <p className="text-xs text-destructive font-medium mr-auto">
            Cannot submit: Insufficient store BV balance.
          </p>
        )}
        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="w-full sm:w-auto"
          disabled={isInsufficientBv || isAdminProduct}
        >
          {isAdminProduct ? "Locked (Admin-Created)" : submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
